import { useEffect, useState } from "react";

import { SvgConverterType } from "../reports/Report";

type Options = {
  svgConverter: SvgConverterType;
  /** Element holding the live chart; mounted only while this returns false. */
  srcId: string;
  /** Container the bitmap is appended into. Wraps the source element. */
  outId: string;
  /** Bitmap width in px. */
  width: number;
  /** False when there is nothing to draw — no pass runs and no bitmap appears. */
  enabled: boolean;
  /** Any value whose change invalidates the bitmap; in practice the language. */
  redrawKey: unknown;
};

/**
 * Swaps a live chart block for a bitmap of itself, and redraws it whenever
 * `redrawKey` changes.
 *
 * html2canvas cannot be relied on to draw inline SVG during PDF export, so these
 * pages rasterize their charts and render the `<img>` in their place — the same
 * pattern the default report's chart pages use. That bakes the chart's text into
 * a bitmap, which is invisible until the text can change: with the language
 * picker, a one-shot swap would strand the captions inside these charts in the
 * previous language while the rest of the page switched. Re-running the pass is
 * what keeps them in step.
 *
 * Returns whether the bitmap is in place.
 */
export const useRasterizedBlock = ({
  svgConverter,
  srcId,
  outId,
  width,
  enabled,
  redrawKey,
}: Options): boolean => {
  const [rasterized, setRasterized] = useState(false);

  // Discard the stale bitmap and let the live block remount; the pass below then
  // runs on the next render, once that block is back in the DOM to be read.
  //
  // Only `<img>` children are removed, never the container's contents wholesale:
  // the source block is React's, and on the first run — which happens on mount,
  // before anything has been drawn — clearing the container would delete the very
  // chart the pass is about to read.
  useEffect(() => {
    setRasterized(false);
    document
      .getElementById(outId)
      ?.querySelectorAll(":scope > img")
      .forEach((img) => img.remove());
  }, [redrawKey, outId]);

  useEffect(() => {
    if (!enabled || rasterized) return;
    let cancelled = false;
    Promise.resolve(svgConverter(srcId, outId, width)).finally(() => {
      if (!cancelled) setRasterized(true);
    });
    return () => {
      cancelled = true;
    };
  }, [svgConverter, srcId, outId, width, enabled, rasterized]);

  return rasterized;
};
